import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OngoingCloseBadge, FilterLabelRow } from '@ui';
import type { BillingTrendPoint } from '@hooks/requests/queries/use_billing_trend_query';

type ChartRow = {
  label: string;
  key: string;
  /** Numeric x position. Real months sit at integer indices (0, 1, 2…) so
   *  they stay evenly spaced and centered under their own tick — the
   *  projection point below uses a fractional index instead of claiming a
   *  full extra category, which is what used to compress/shift every real
   *  month left of where Figma places it. */
  xIndex: number;
  closed: number | undefined;
  ongoing: number | undefined;
  isProjection?: boolean;
};

// Half-band padding applied on each side of the plotted range, mirroring the
// spacing confirmed in Figma (node 613:1840): month label/dot centers sit
// inset from the grid edges, and the dashed "ongoing" tail extends exactly
// into that same right-side padding rather than adding a whole extra month
// column. Keeping this as a plain fraction (not a full 1.0 step) is what
// keeps every real month centered on its own label.
const AXIS_EDGE_PADDING = 0.5;

type UsageOverTimeChartProps = {
  /** Billing data aggregated by month (primary source). */
  billingData: BillingTrendPoint[];
  /** Omit for Partner roles — a single-org caller has no "which partner" to show. */
  orgLabel?: string;
  /** Set when a specific company is selected — shown as second segment in filter row. */
  companyLabel?: string;
  isLoading?: boolean;
};

const getCurrentMonthKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// "Dec 2025" / "Jun 2026" — used in the filter row date range text
const formatMonthKey = (yyyyMM: string): string => {
  const [y, m] = yyyyMM.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// "Dec 2025 – May 2026" derived from actual billing data (not the filter preset label)
const formatMonthRange = (months: BillingTrendPoint[]): string => {
  if (months.length === 0) return '';
  if (months.length === 1) return formatMonthKey(months[0].month);
  return `${formatMonthKey(months[0].month)} – ${formatMonthKey(months[months.length - 1].month)}`;
};

// Y-axis tick: comma-separated < 10K, K-abbreviated ≥ 10K
const formatYTick = (value: number): string => {
  if (value >= 10000) {
    const k = value / 1000;
    return `${k % 1 === 0 ? k : k.toFixed(1)}K`;
  }
  return value.toLocaleString();
};

// Y domain: ~10% padding, rounded to nearest 100; always at least a 100-unit span
const computeYDomain = (data: BillingTrendPoint[]): [number, number] => {
  const values = data.map((p) => p?.maxUserCount || 0).filter((v) => v > 0);
  if (values.length === 0) return [0, 400];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const domainMin = Math.max(0, Math.floor((min * 0.9) / 100) * 100);
  const domainMax = Math.ceil((max * 1.1) / 100) * 100;
  return [domainMin, Math.max(domainMax, domainMin + 100)];
};

// State 3: only trim leading zeros when ALL prior months are zero (company just joined).
// If any past month has data, show the full window including zero months.
const trimLeadingZeros = (data: BillingTrendPoint[]): BillingTrendPoint[] => {
  if (data.length === 0) return data;
  const priorMonths = data.slice(0, -1); // everything except current month
  const allPriorZero = priorMonths.every((p) => p.maxUserCount === 0);
  if (!allPriorZero) return data; // history exists — show all months as-is
  // Truly new company — start from first non-zero month
  const firstIdx = data.findIndex((p) => p.maxUserCount > 0);
  return firstIdx > 0 ? data.slice(firstIdx) : data;
};

// ── Dots ──────────────────────────────────────────────────────────────────────
// Prototype markers: a 3.5px HWM dot on a 5px card-coloured halo; the current
// (ongoing) month is hollowed out. Colours are DS tokens so they follow the
// light/dark token remap.
const ClosedDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (payload.closed === undefined || payload.isProjection) return null;
  const isCurrent = payload.key === getCurrentMonthKey();

  return (
    <g key={`dot-g-${cx}-${cy}`}>
      <circle cx={cx} cy={cy} r={5} fill="var(--au-surface-card)" />
      <circle cx={cx} cy={cy} r={3.5} fill="var(--au-feedback-warning)" />
      {isCurrent && <circle cx={cx} cy={cy} r={1.8} fill="var(--au-surface-card)" />}
    </g>
  );
};

// ── Tooltip ───────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as ChartRow | undefined;
  if (row?.isProjection) return null;
  const value = payload.find((p) => p.value !== undefined)?.value;
  // Read the label off the row itself rather than recharts' `label` prop —
  // the axis is now a numeric scale (see AXIS_EDGE_PADDING above), so the
  // built-in label would be a raw x-index, not the month text.
  return (
    <div className="suc-tooltip">
      <p className="suc-tooltip-label">{row?.label}</p>
      <p className="suc-tooltip-value">{value !== undefined ? formatYTick(value) : '—'} users</p>
    </div>
  );
};

// ── X-axis tick ───────────────────────────────────────────────────────────────
const CustomXTick = ({ x, y, payload, labelsByIndex }: any) => {
  const text = labelsByIndex?.[payload.value] ?? '';
  return (
    <text
      x={x}
      y={y + 8}
      textAnchor="middle"
      fill="var(--au-text-subtle)"
      fontSize={11}
      fontFamily="Rubik, sans-serif"
    >
      {text}
    </text>
  );
};

// ── Legend (prototype `.legend`) ─────────────────────────────────────────────
const Legend: React.FC<{ showOngoing: boolean }> = ({ showOngoing }) => (
  <div className="suc-legend">
    <div className="suc-legend-item">
      <svg width="16" height="2" viewBox="0 0 16 2" aria-hidden="true">
        <rect width="16" height="2" rx="1" fill="var(--au-brand-tone)" />
      </svg>
      <span className="suc-legend-label">Provisioned</span>
    </div>
    <div className="suc-legend-item">
      <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
        <circle cx="4" cy="4" r="4" fill="var(--au-feedback-warning)" />
      </svg>
      <span className="suc-legend-label">HWM&nbsp;·&nbsp;closed</span>
    </div>
    {showOngoing && (
      <div className="suc-legend-item">
        <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
          <circle
            cx="4"
            cy="4"
            r="3.25"
            fill="none"
            stroke="var(--au-feedback-warning)"
            strokeWidth="1.5"
          />
        </svg>
        <span className="suc-legend-label">HWM&nbsp;·&nbsp;ongoing</span>
      </div>
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const UsageOverTimeChart: React.FC<UsageOverTimeChartProps> = ({
  billingData,
  orgLabel,
  companyLabel,
  isLoading = false,
}) => {
  const { lineData, hasCurrentMonth, labelsByIndex, monthTicks, xDomain, yDomain, dateLabel } =
    useMemo(() => {
      const curKey = getCurrentMonthKey();

      // State 3: trim leading zeros so new-company charts start from first real month
      const effectiveData = trimLeadingZeros(billingData);

      const hasCurrentMonth =
        effectiveData.length > 0 && effectiveData[effectiveData.length - 1].month === curKey;
      const lastRealIndex = Math.max(effectiveData.length - 1, 0);

      // Build chart rows — closed = all months incl. current; ongoing = dashed tail.
      // Real months sit at plain integer x-indices (0, 1, 2…) so they stay evenly
      // spaced and centered under their own tick label, matching Figma.
      const lineData: ChartRow[] = effectiveData.map((p, i) => ({
        label: p.label,
        key: p.month,
        xIndex: i,
        closed: p.maxUserCount,
        ongoing: hasCurrentMonth && p.month === curKey ? p.maxUserCount : undefined,
      }));

      // Silent projection point — extends the dashed line into the right-edge
      // padding (see AXIS_EDGE_PADDING), not into a whole new category. This
      // keeps every real month's dot centered on its own label instead of
      // getting compressed left to make room for a same-width extra column.
      if (hasCurrentMonth && effectiveData.length > 0) {
        const last = effectiveData[effectiveData.length - 1];
        const [y, m] = last.month.split('-').map(Number);
        const next = new Date(y, m, 1); // m is 1-indexed here so this advances correctly
        const nextKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
        lineData.push({
          label: '',
          key: nextKey,
          xIndex: lastRealIndex + AXIS_EDGE_PADDING,
          closed: undefined,
          ongoing: last.maxUserCount,
          isProjection: true,
        });
      }

      const labelsByIndex = effectiveData.map((p) => p.label);
      const monthTicks = effectiveData.map((_, i) => i);
      const xDomain: [number, number] = [-AXIS_EDGE_PADDING, lastRealIndex + AXIS_EDGE_PADDING];
      const yDomain = computeYDomain(effectiveData);
      // Always show the actual month range derived from data (never the filter preset label)
      const dateLabel = formatMonthRange(effectiveData);

      return {
        lineData,
        hasCurrentMonth,
        labelsByIndex,
        monthTicks,
        xDomain,
        yDomain,
        dateLabel,
      };
    }, [billingData]);

  return (
    <div className="suc-card">
      {/* Top row: eyebrow + legend */}
      <div className="suc-top-row">
        <div>
          <div className="suc-eyebrow">Usage over time</div>
          <div className="suc-filter-row">
            <FilterLabelRow orgLabel={orgLabel} companyLabel={companyLabel} dateLabel={dateLabel} />
            <OngoingCloseBadge isOngoing={hasCurrentMonth} />
          </div>
        </div>
        <Legend showOngoing={hasCurrentMonth} />
      </div>

      {/* Loading state */}
      {isLoading && <div className="suc-loading">Loading billing data…</div>}

      {/* Chart */}
      {!isLoading && (
        <div className="suc-chart-wrap">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lineData} margin={{ top: 12, right: 8, left: -8, bottom: 0 }}>
              {/* Horizontal guides only (prototype: solid, border-default) */}
              <CartesianGrid
                vertical={false}
                stroke="var(--au-border-default)"
                strokeDasharray="0"
              />
              <XAxis
                dataKey="xIndex"
                type="number"
                domain={xDomain}
                ticks={monthTicks}
                axisLine={false}
                tickLine={false}
                tick={(props) => <CustomXTick {...props} labelsByIndex={labelsByIndex} />}
                dy={2}
              />
              <YAxis
                allowDecimals={false}
                tickFormatter={formatYTick}
                tick={{
                  fontSize: 11,
                  fill: 'var(--au-text-subtle)',
                  fontFamily: 'Rubik, sans-serif',
                }}
                axisLine={false}
                tickLine={false}
                domain={yDomain}
                tickCount={5}
                width={44}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: 'var(--au-border-default)',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                }}
              />

              {/* Solid line — all months including current */}
              <Line
                type="monotone"
                dataKey="closed"
                stroke="var(--au-brand-tone)"
                strokeWidth={2}
                strokeLinecap="round"
                dot={<ClosedDot />}
                activeDot={{
                  r: 4.75,
                  fill: 'var(--au-feedback-warning)',
                  stroke: 'var(--au-surface-card)',
                  strokeWidth: 1.75,
                }}
                connectNulls={false}
                legendType="none"
              />

              {/* Dashed tail — current month → silent projection point */}
              {hasCurrentMonth && (
                <Line
                  type="monotone"
                  dataKey="ongoing"
                  stroke="var(--au-brand-tone)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeDasharray="4 4"
                  dot={false}
                  activeDot={false}
                  connectNulls={false}
                  legendType="none"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default UsageOverTimeChart;
